import { Button, Modal } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';

export type ApprovalVerb = 'approve' | 'reject' | 'revoke';

type Props = {
  readonly verb: ApprovalVerb;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
};

/**
 * One dialog, driven by the verb prop, rather than three near-identical ones
 * (BL-112) — the approve confirmation carries the load-bearing copy stating
 * no key is shown, so it shares the same shape as reject/revoke rather than
 * firing without confirmation.
 */
export function ApprovalDialog({ verb, busy, error, onConfirm, onClose }: Props) {
  const t = useTranslations('pages.admin.fleet');

  const copy = {
    approve: {
      title: t('approve-title'),
      body: t('approve-body'),
      cta: t('approve-cta'),
      variant: 'primary' as const,
    },
    reject: {
      title: t('reject-title'),
      body: t('reject-body'),
      cta: t('reject-cta'),
      variant: 'danger' as const,
    },
    revoke: {
      title: t('revoke-title'),
      body: t('revoke-body'),
      cta: t('revoke-cta'),
      variant: 'danger' as const,
    },
  }[verb];

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      confirmOnClose={false}
      size="sm"
      title={copy.title}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            {t('cancel-cta')}
          </Button>
          <Button type="button" variant={copy.variant} onClick={onConfirm} disabled={busy}>
            {copy.cta}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-secondary">{copy.body}</p>
      {error !== null && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {error}
        </p>
      )}
    </Modal>
  );
}
